import * as React from 'react'
import { Popover, Tooltip } from '@mui/material'
import {
  CloseRounded, KeyboardArrowUpRounded, KeyboardArrowDownRounded, OpenInNew, DeleteOutline, PersonAddAltOutlined,
  CheckCircleOutline, ReplayRounded, TimerOutlined, TuneRounded, OpenInFullRounded, CloseFullscreenRounded, DescriptionOutlined,
} from '@mui/icons-material'
import { useDispatch } from '../../store'
import { deleteTicket, editPriority, editStatus, editTicket, startTimer } from '../../store/support/actionCreator'
import { Ticket, TicketType } from '../../store/support/reducer'
import { Admin } from '../../store/user/reducer'
import { EnumTicketStatus } from '../../enum/statusTicket'
import { formatDate } from '../../function/date'
import { ConfirmV2 } from '../ConfirmV2'
import { AttachmentsV2 } from './AttachmentsV2'
import { ChatV2 } from './ChatV2'
import {
  dueLabel, personName, PRIORITIES, PRIORITY_LABEL, PRIORITY_TONE, STATUSES, STATUS_LABEL, STATUS_TONE, typeColor,
} from './supportTickets'
import s from './SupportV2.module.css'

/*
 * One ticket, beside the queue. Status and priority save at once (PATCH
 * /ticket/status, /ticket/priority); type and assignee save together, as the
 * backend wants both on every PUT /ticket/:id (the classic popup's Save);
 * the due time starts the ticket's clock (PATCH /ticket/time) and sets
 * `due` — the classic popup imported that but had no control for it.
 * Changes are applied to the queue as soon as the server says yes.
 */

const FOCUS_KEY = 'supportChatFocus'

const DUES: { label: string; days: number; hours: number }[] = [
  { label: '4 h', days: 0, hours: 4 }, { label: '1 day', days: 1, hours: 0 }, { label: '3 days', days: 3, hours: 0 }, { label: '1 week', days: 7, hours: 0 },
]

/* text cut with an ellipsis when it doesn't fit; the whole text in a tooltip, only when it is cut */
function Trunc({ text, className }: { text: string; className?: string }) {
  const [cut, setCut] = React.useState(false)
  return (
    <Tooltip title={cut ? text : ''}>
      <span className={`${s.trunc} ${className || ''}`} onMouseEnter={(e) => setCut(e.currentTarget.scrollWidth > e.currentTarget.clientWidth)}>{text}</span>
    </Tooltip>
  )
}

/* a select that shows its value as a coloured pill; the field's name is in the tooltip */
function Pill({ value, tone, options, onChange, label, empty }: {
  value: string; tone: string; options: { value: string; label: string }[]; onChange: (v: string) => void; label: string; empty: string
}) {
  return (
    <Tooltip title={label}>
      <select
        className={s.pill}
        aria-label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ color: tone, background: `color-mix(in srgb, ${tone} 12%, transparent)`, borderColor: `color-mix(in srgb, ${tone} 30%, transparent)` }}
      >
        {!value && <option value="">{empty}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Tooltip>
  )
}

export function TicketPanelV2({ ticket, admins, types, meUid, onPatch, onDeleted, onClose, onPrev, onNext, onManageTypes }: {
  ticket: Ticket
  admins: Admin[]
  types: TicketType[]
  meUid: string
  onPatch: (t: Ticket) => void
  onDeleted: (id: number) => void
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  onManageTypes: () => void
}) {
  const dispatch = useDispatch()
  const [typeId, setTypeId] = React.useState<number | ''>(ticket.ticketType?.id ?? '')
  const [assignee, setAssignee] = React.useState<string>(ticket.assignee?.uid || '')
  const [askDelete, setAskDelete] = React.useState(false)
  const [dueOpen, setDueOpen] = React.useState(false)
  const [custom, setCustom] = React.useState({ days: '0', hours: '8' })
  const [hint, setHint] = React.useState('')
  // chat focus: the rest folds into one strip; kept in this browser, so the next ticket opens the same way
  const [focus, setFocusState] = React.useState(() => { try { return localStorage.getItem(FOCUS_KEY) === '1' } catch { return false } })
  const setFocus = (v: boolean) => {
    setFocusState(v)
    try { localStorage.setItem(FOCUS_KEY, v ? '1' : '0') } catch { /* private mode */ }
  }
  const [requestAnchor, setRequestAnchor] = React.useState<HTMLElement | null>(null)

  // in focus, Esc folds the chat back first (the page's Esc would close the ticket)
  React.useEffect(() => {
    if (!focus) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const el = e.target as HTMLElement
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) return
      if (document.querySelector('.MuiDialog-root') || requestAnchor) return
      e.stopPropagation()
      setFocus(false)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  })

  React.useEffect(() => {
    setTypeId(ticket.ticketType?.id ?? '')
    setAssignee(ticket.assignee?.uid || '')
    setHint('')
    setDueOpen(false)
  }, [ticket.id, ticket.ticketType?.id, ticket.assignee?.uid])

  const customer = personName(ticket.user) || 'Customer'
  const due = dueLabel(ticket)
  const closed = ticket.status === EnumTicketStatus.CLOSED

  function setStatus(status: string) {
    dispatch(editStatus(ticket.id, status, () => onPatch({ ...ticket, status: status as Ticket['status'] })))
  }
  function setPriority(priority: string) {
    dispatch(editPriority(ticket.id, priority, () => onPatch({ ...ticket, priority: priority as Ticket['priority'] })))
  }
  // type and assignee go together — the backend needs both on every save
  function saveTypeAssignee(nextType: number | '', nextAssignee: string) {
    if (nextType === '' || !nextAssignee) {
      setHint(nextType === '' ? 'Choose a type too — it saves with the assignee' : 'Choose an assignee too — it saves with the type')
      return
    }
    setHint('')
    const body = { ticketTypeId: nextType, assigneeUid: nextAssignee, status: ticket.status, priority: ticket.priority }
    dispatch(editTicket(ticket.id, body, () => onPatch({
      ...ticket,
      ticketType: types.find((x) => x.id === nextType) || ticket.ticketType,
      assignee: admins.find((a) => a.uid === nextAssignee) || ticket.assignee,
    })))
  }
  function setDue(days: number, hours: number) {
    if (typeId === '' || !assignee) { setHint('Set the type and the assignee first — the due time saves with them'); return }
    const body = { ticketTypeId: typeId, assigneeUid: assignee, status: ticket.status, priority: ticket.priority, due: { days, hours } }
    dispatch(startTimer(ticket.id, body, () => onPatch({ ...ticket, due: { days, hours }, startedTime: new Date().toISOString() })))
    setDueOpen(false)
  }

  const meAdmin = admins.find((a) => a.uid === meUid)

  return (
    <aside className={`${s.panel} ${focus ? s.panelFocus : ''}`}>
      {focus ? (
        <div className={s.focusBar}>
          <span className={s.idTag}>#{ticket.id}</span>
          <Trunc text={ticket.title} className={s.focusTitle} />
          <span className={s.focusWho}>
            <span className={s.focusAvatar}>{ticket.user?.avatar ? <img src={ticket.user.avatar} alt="" /> : (customer[0] || '?').toUpperCase()}</span>
            <Trunc text={customer} />
          </span>
          <Pill label="Status" empty="No status" value={ticket.status} tone={STATUS_TONE[ticket.status] || 'var(--text2)'} options={STATUSES.map((v) => ({ value: v, label: STATUS_LABEL[v] }))} onChange={setStatus} />
          <Pill label="Priority" empty="No priority" value={ticket.priority} tone={PRIORITY_TONE[ticket.priority] || 'var(--text2)'} options={PRIORITIES.map((v) => ({ value: v, label: PRIORITY_LABEL[v] }))} onChange={setPriority} />
          <button type="button" className={`${s.ghostSm} ${requestAnchor ? s.ghostOn : ''}`} onClick={(e) => setRequestAnchor(e.currentTarget)}>
            <DescriptionOutlined sx={{ fontSize: 15 }} />Request{ticket.attachments?.length ? ` · ${ticket.attachments.length}` : ''}
          </button>
          <span className={s.spacer} />
          {closed
            ? <button type="button" className={s.ghostSm} onClick={() => setStatus(EnumTicketStatus.OPEN)}><ReplayRounded sx={{ fontSize: 15 }} />Reopen</button>
            : <button type="button" className={s.okSm} onClick={() => setStatus(EnumTicketStatus.CLOSED)}><CheckCircleOutline sx={{ fontSize: 15 }} />Close</button>}
          <Tooltip title="Previous (K)"><span><button type="button" className={s.iconGhost} onClick={onPrev} disabled={!onPrev} aria-label="Previous"><KeyboardArrowUpRounded sx={{ fontSize: 18 }} /></button></span></Tooltip>
          <Tooltip title="Next (J)"><span><button type="button" className={s.iconGhost} onClick={onNext} disabled={!onNext} aria-label="Next"><KeyboardArrowDownRounded sx={{ fontSize: 18 }} /></button></span></Tooltip>
          <Tooltip title="Show the ticket (Esc)"><button type="button" className={s.collapseBtn} onClick={() => setFocus(false)}><CloseFullscreenRounded sx={{ fontSize: 15 }} />Collapse</button></Tooltip>
          <Tooltip title="Close the ticket panel"><button type="button" className={s.iconGhost} onClick={onClose} aria-label="Close"><CloseRounded sx={{ fontSize: 18 }} /></button></Tooltip>
          <Popover
            open={!!requestAnchor}
            anchorEl={requestAnchor}
            onClose={() => setRequestAnchor(null)}
            disablePortal
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ className: s.requestPop }}
          >
            <small>Request</small>
            <p>{ticket.summary}</p>
            {!!ticket.attachments?.length && <AttachmentsV2 items={ticket.attachments} label />}
          </Popover>
        </div>
      ) : (
      <div className={s.panelTop}>
        <div className={s.panelTitle}>
          <span className={s.idTag}>#{ticket.id}</span>
          <b title={ticket.title}>{ticket.title}</b>
        </div>
        <div className={s.panelNav}>
          <Tooltip title="Previous (K)"><span><button type="button" className={s.iconGhost} onClick={onPrev} disabled={!onPrev} aria-label="Previous"><KeyboardArrowUpRounded sx={{ fontSize: 18 }} /></button></span></Tooltip>
          <Tooltip title="Next (J)"><span><button type="button" className={s.iconGhost} onClick={onNext} disabled={!onNext} aria-label="Next"><KeyboardArrowDownRounded sx={{ fontSize: 18 }} /></button></span></Tooltip>
          <Tooltip title="Close (Esc)"><button type="button" className={s.iconGhost} onClick={onClose} aria-label="Close"><CloseRounded sx={{ fontSize: 18 }} /></button></Tooltip>
        </div>
      </div>
      )}

      <div className={s.panelBody}>
        {!focus && (
        <>
        <div className={s.who}>
          <span className={s.whoAvatar}>{ticket.user?.avatar ? <img src={ticket.user.avatar} alt="" /> : (customer[0] || '?').toUpperCase()}</span>
          <div>
            <b>{customer}</b>
            <span>{ticket.user?.email}{ticket.user?.package?.title ? ` · ${ticket.user.package.title}` : ''}</span>
          </div>
          {ticket.user?.id != null && (
            <Tooltip title="Open the customer’s page">
              <button type="button" className={s.iconGhost} onClick={() => window.open(`/users/subscriber/${ticket.user.id}`, '_blank', 'noopener,noreferrer')} aria-label="Open the customer’s page"><OpenInNew sx={{ fontSize: 15 }} /></button>
            </Tooltip>
          )}
          <span className={s.whoDate}>{formatDate(ticket.createdTime, 'dd.MM.yyyy HH:mm')}</span>
        </div>

        {/* the ticket's fields and its actions — one row, wrapping on a narrow panel */}
        <div className={s.bar}>
          <Pill label="Status" empty="No status" value={ticket.status} tone={STATUS_TONE[ticket.status] || 'var(--text2)'} options={STATUSES.map((v) => ({ value: v, label: STATUS_LABEL[v] }))} onChange={setStatus} />
          <Pill label="Priority" empty="No priority" value={ticket.priority} tone={PRIORITY_TONE[ticket.priority] || 'var(--text2)'} options={PRIORITIES.map((v) => ({ value: v, label: PRIORITY_LABEL[v] }))} onChange={setPriority} />
          <Tooltip title="Assignee">
            <select className={s.select} aria-label="Assignee" value={assignee} onChange={(e) => { setAssignee(e.target.value); saveTypeAssignee(typeId, e.target.value) }}>
              <option value="">Unassigned</option>
              {admins.map((a) => <option key={a.uid} value={a.uid}>{personName(a) || a.email}{a.uid === meUid ? ' (you)' : ''}</option>)}
            </select>
          </Tooltip>
          <span className={s.typeSelect}>
            <i style={{ background: typeColor(types.find((x) => x.id === typeId)?.color) }} />
            <Tooltip title="Type">
              <select className={s.select} aria-label="Type" value={typeId} onChange={(e) => { const v = e.target.value ? Number(e.target.value) : ''; setTypeId(v); saveTypeAssignee(v, assignee) }}>
                <option value="">No type</option>
                {types.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </Tooltip>
            <Tooltip title="Manage types"><button type="button" className={s.linkBtn} onClick={onManageTypes} aria-label="Manage types"><TuneRounded sx={{ fontSize: 15 }} /></button></Tooltip>
          </span>
          <span className={s.barSep} />
          {!closed && meAdmin && ticket.assignee?.uid !== meUid && (
            <button type="button" className={s.ghostSm} onClick={() => { setAssignee(meUid); saveTypeAssignee(typeId, meUid) }}><PersonAddAltOutlined sx={{ fontSize: 15 }} />Take it</button>
          )}
          {closed
            ? <button type="button" className={s.ghostSm} onClick={() => setStatus(EnumTicketStatus.OPEN)}><ReplayRounded sx={{ fontSize: 15 }} />Reopen</button>
            : <button type="button" className={s.okSm} onClick={() => setStatus(EnumTicketStatus.CLOSED)}><CheckCircleOutline sx={{ fontSize: 15 }} />Close</button>}
          <span className={s.dueWrap}>
            <button type="button" className={`${s.ghostSm} ${due?.late ? s.late : ''}`} onClick={() => setDueOpen(!dueOpen)} disabled={closed}>
              <TimerOutlined sx={{ fontSize: 15 }} />{due ? due.text : 'Set due'}
            </button>
            {dueOpen && (
              <span className={s.duePop}>
                <small>Due in — counted from now</small>
                <span className={s.dueChips}>{DUES.map((d) => <button key={d.label} type="button" className={s.chip} onClick={() => setDue(d.days, d.hours)}>{d.label}</button>)}</span>
                <span className={s.dueCustom}>
                  <input type="number" min={0} value={custom.days} onChange={(e) => setCustom({ ...custom, days: e.target.value })} />d
                  <input type="number" min={0} max={23} value={custom.hours} onChange={(e) => setCustom({ ...custom, hours: e.target.value })} />h
                  <button type="button" className={s.primarySm} onClick={() => setDue(Math.max(0, Number(custom.days) || 0), Math.max(0, Number(custom.hours) || 0))}>Set</button>
                </span>
              </span>
            )}
          </span>
          <span className={s.spacer} />
          <Tooltip title="Delete the ticket"><button type="button" className={s.iconDanger} onClick={() => setAskDelete(true)} aria-label="Delete the ticket"><DeleteOutline sx={{ fontSize: 17 }} /></button></Tooltip>
        </div>
        {hint && <div className={s.hint}>{hint}</div>}

        <div className={s.summary}>
          <small>Request</small>
          <p>{ticket.summary}</p>
          {!!ticket.attachments?.length && (
            <div className={s.files}><AttachmentsV2 items={ticket.attachments} label /></div>
          )}
        </div>

        <div className={s.chatHead}>
          <span>Conversation</span>
          <button type="button" className={s.ghostSm} onClick={() => setFocus(true)}><OpenInFullRounded sx={{ fontSize: 14 }} />Expand chat</button>
        </div>
        </>
        )}

        <ChatV2 ticketId={ticket.id} customerName={customer} />
      </div>

      {askDelete && (
        <ConfirmV2
          header={`Delete ticket #${ticket.id}? The conversation goes with it.`}
          onYes={() => dispatch(deleteTicket(ticket.id, () => onDeleted(ticket.id)))}
          onClose={() => setAskDelete(false)}
        />
      )}
    </aside>
  )
}
